<template>
	<div
		class="navbar container-fluid container-max-width"
		:class="{
			'bg-dark-brown': isNetflixStatisticPage,
			'landing-page': isHome && isGlobal,
			'navbar-hide': (isHome && isUnsupportedCountry) || isStreamingCharts,
		}"
	>
		<div style="display: flex; width: 100%">
			<div class="navbar__logo" @click="trackLogoClick">
				<SmartLink :to="{ name: 'app.home' }">
					<div class="navbar__logo-link">
						<picture>
							<source type="image/webp" :srcset="logoSrc.webp" />
							<img :src="logoSrc.png" alt="JustWatch" />
						</picture>

						<FreemiumFilterBadge v-show="isPremium" />
					</div>
				</SmartLink>
			</div>

			<div class="navbar__wrapper" data-testid="navbar">
				<SmartLink
					@click.native="onTabChange('home')"
					:to="{ name: 'app.home' }"
					class="navbar__button__link navbar__button--home landing-hide"
					data-testid="navbar-home"
				>
					<span class="navbar__button__link__item">{{ $t('WEBAPP_MENU_HOME') }}</span>
				</SmartLink>

				<SmartLink
					@click.native="onTabChange('new')"
					class="navbar__button__link"
					:to="{ ...routeParams(CollectionType.NEW) }"
					data-testid="navbar-new"
				>
					<!-- name is sometimes null and that crashes server-->

					<span class="navbar__button__link__item">{{ $t('WEBAPP_MENU_NEW') }}</span>
				</SmartLink>

				<SmartLink
					@click.native="onTabChange('popular')"
					class="navbar__button__link"
					:to="{ ...routeParams(CollectionType.POPULAR) }"
					data-testid="navbar-popular"
				>
					<span class="navbar__button__link__item">{{ $t('WEBAPP_MENU_POPULAR') }}</span>
				</SmartLink>

				<SmartLink
					v-if="shouldShowWatchInHeader"
					@click.native="onTabChange('watch')"
					class="navbar__button__link"
					:to="`/${webLocale}/watch`"
					data-testid="navbar-sports"
				>
					<span class="navbar__button__link__item">Watch</span>
				</SmartLink>

				<span class="navbar__button navbar__button--watchlist landing-hide">
					<SmartLink
						@click.native="onTabChange('watchlist')"
						class="navbar__button__link"
						:to="{ ...routeParams(listsCollectionTypeToNavigate) }"
						data-testid="navbar-watchlist"
					>
						<span class="navbar__button__link__item">
							{{ $t('WEBAPP_LISTS') }}
						</span>

						<FontAwesomeIcon :icon="faBookmark" class="navbar__button__icon bookmark" />
					</SmartLink>
				</span>

				<SmartLink
					v-if="shouldShowSportsInHeader"
					@click.native="onTabChange('sports')"
					class="navbar__button__link navbar__button--sports"
					:class="{ 'router-link-exact-active': isSports }"
					:to="{ name: 'app.sports.subdomain.all', ...routeParams(CollectionType.SPORTS) }"
					data-testid="navbar-sports"
				>
					<span class="navbar__button__link__item">{{ $t('WEBAPP_SPORTS') }}</span>
				</SmartLink>

				<SmartLink
					v-if="shouldShowGuideInHeader"
					@click.native="onTabChange('guide')"
					class="navbar__button__link navbar__button--guide"
					:class="{
						'nav-link-neutral': !isEditorial,
						'navbar__button--guide-hidden-mobile': !shouldShowGuideInHeaderMobile,
					}"
					:to="{ name: 'app.editorial.subdomain.home' }"
					data-testid="navbar-guide"
				>
					<span class="navbar__button__link__item guide">{{ $t('WEBAPP_GUIDE') }}</span>
				</SmartLink>

				<div class="navbar__search" :class="{ 'navbar__search--expanded': isSearchSuggesterOpen }">
					<SearchSuggesterBar :collectionType="lastActiveCollectionType" />

					<div class="search-overlay" v-if="isSearchSuggesterOpen" @click="onOverlayClick"></div>
				</div>

				<div class="navbar__extra" :class="{ 'navbar__extra--remove-spacing': isLoggedIn }">
					<LoginMenuButtonNew class="navbar__button" :dark="true" />

					<SettingsButton class="navbar__button" v-if="!isHome || !isGlobal" />
				</div>

				<div class="navbar__extra__small">
					<div class="navbar__extra__small--left">
						<SettingsButton class="navbar__button" />
					</div>

					<div class="navbar__extra__small--right">
						<LoginMenuButton class="navbar__button" style="width: 50px" />
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent } from 'vue';

const FreemiumFilterBadge = defineAsyncComponent(
	() => import(/* webpackChunkName: "freemium-filter-badge" */ '@/components/filter-bar/FreemiumFilterBadge.vue')
);
import LoginMenuButton from '@/components/nav/LoginMenuButton.vue';
import LoginMenuButtonNew from '@/components/nav/LoginMenuButtonNew.vue';
import SettingsButton from '@/components/nav/static-pages/SettingsButton.vue';
import SearchSuggesterBar from '@/features/search/components/SearchSuggesterBar.vue';

import { CollectionType } from '@/enums/collection-type';
import { OnboardingStatus } from '@/enums/onboarding-status';

import { getRouteName, getUrlParamsFromFilterState } from '@/helpers/state-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { faBookmark } from '@fortawesome/free-solid-svg-icons';

import { useSearch } from '@/features/search/composables/useSearch';
import { storeToRefs } from 'pinia';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useConstantStore } from '@/stores/pinia/useConstantStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useFilterStore } from '@/stores/pinia/useFilterStore';
import type { FilterCollection } from '@/stores/useFilterStoreTypes';
import { getNavItemVisibility } from '@/helpers/navigation-helper';
import { isEditorialLocale } from '@/routing/editorial';

const emits = defineEmits(['tabChange']);

const { subgenresList, providersByShortName, genresByShortName } = storeToRefs(useConstantStore());
const { filters } = useFilterStore();
const { webLocale } = storeToRefs(useLanguageStore());
const { lastUrlMetadata, lastActiveListsCollectionType, lastActiveCollectionType } = storeToRefs(useRoutingStore());
const { onboardingStatus, isLoggedIn, isPremium } = storeToRefs(useUserStore());

const { isSearchSuggesterOpen, setSearchSuggesterVisibility } = useSearch();
const route = useRoute();

// Navigation visibility
const shouldShowWatchInHeader = computed(() => getNavItemVisibility(webLocale.value, 'watch', 'header').desktop);
const shouldShowGuideInHeader = computed(() => isEditorialLocale(webLocale.value));
const shouldShowSportsInHeader = computed(() => getNavItemVisibility(webLocale.value, 'sports', 'header').desktop);
const shouldShowGuideInHeaderMobile = computed(() => getNavItemVisibility(webLocale.value, 'guide', 'header').mobile);

const isNewTab = computed(() => {
	const newPageRoutes = ['app.titles.new', 'app.titles.upcoming', 'app.titles.pricedrops'];
	return newPageRoutes.find(routeName => (route.name || '').toString().startsWith(routeName));
});

const isNetflixStatisticPage = computed(() => route.name === 'app.landingpage.statistics');

const isHome = computed(() => route.name === 'app.home');

const isGlobal = computed(() => onboardingStatus.value === OnboardingStatus.GLOBAL);

const isUnsupportedCountry = computed(() => onboardingStatus.value === OnboardingStatus.UNSUPPORTED_COUNTRY);

const isStreamingCharts = computed(
	() => route.name === 'app.streaming-charts' || /\/streaming-charts\/?$/.test(route.path)
);

const isEditorial = computed(() => (route.name || '').toString().startsWith('app.editorial'));

const isSports = computed(() => (route.name || '').toString().startsWith('app.sports'));

const logoSrc = computed(() => {
	const file = `JustWatch-logo-${isGlobal.value ? 'large' : 'small'}`;
	return {
		png: `/${ASSETS_DIR}/img/logo/${file}.png`,
		webp: `/${ASSETS_DIR}/img/logo/${file}.webp`,
	};
});

const listsCollectionTypeToNavigate = computed(() => {
	if (lastActiveListsCollectionType.value) {
		return lastActiveListsCollectionType.value;
	}

	return isLoggedIn.value ? CollectionType.MY_LISTS : CollectionType.PUBLIC_LISTS;
});

/**
 * with these parameters we can always link to the correct url for each collectionType page.
 * in some cases we want to overwrite the currentFilters of the passed CollectionType, that's what `filterOverwrite` handles.
 */
function routeParams(collectionType: CollectionType, filterOverwrite?: FilterCollection): any {
	const isNewPageCollectionType = [
		CollectionType.COMING_SOON,
		CollectionType.LEAVING_SOON,
		CollectionType.PRICEDROPS,
	].includes(collectionType);

	const filterCollectionType = isNewPageCollectionType ? CollectionType.NEW : collectionType;
	const _filters = filterOverwrite ?? filters(filterCollectionType);
	const routeName = getRouteName(collectionType, _filters, subgenresList.value);
	const params = getUrlParamsFromFilterState(
		routeName,
		_filters,
		providersByShortName.value,
		genresByShortName.value,
		collectionType,
		route.query || {},
		subgenresList.value
	);

	// Note: list_id query param needs to be deleted when navigating to the Lists tab root level, since we are
	// using it on Lists tab to determinate if list details page is opened
	if (params.query.list_id) {
		delete params.query.list_id;
	}

	return { name: routeName, ...params };
}

async function onTabChange(label: string) {
	emits('tabChange');
	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'tabchange',
			label,
			useLastRoute: true,
		},
		[],
		false,
		// Fire immediately to prevent losing events when navigating to different domain
		true
	);
}

function trackLogoClick() {
	// If you click the JW Logo on your first page visit, there won't be a "last route"
	// This had forced the page type logic in the tracker to depend on useRoute or $route,
	// both of which depend on a Vue instance which isn't available there (as of now).
	// Now, if you click it before navigating, it'll use the current route metadata.
	const useLastRoute = lastUrlMetadata != null;

	TrackingHelper.trackEvent(
		'userinteraction',
		{
			action: 'tabchange',
			label: 'click_logo',
			useLastRoute,
		},
		[],
		false,
		// Fire immediately to prevent losing events when navigating to different domain
		true
	);
}

function onOverlayClick() {
	setSearchSuggesterVisibility(false);
}
</script>

<style lang="scss">
@import '~@/styles/variables.scss';
@import '~@/styles/rtl-support-mixins';

.navbar__button__link__item {
	text-transform: capitalize;
}

.search-overlay {
	position: fixed;
	display: block;
	width: 100vw;
	height: 100vh;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	cursor: pointer;
}

.navbar {
	position: sticky;
	display: flex;
	background: $jw-color-darkblue--dark;
	height: 56px;
	width: 100%;
	top: 0;
	margin: 0 auto;
	z-index: 100;

	@media (max-width: $breakpoint-two-rows) {
		max-height: 105px;
	}

	&__wrapper {
		display: flex;
		justify-content: flex-end;
		width: 100%;
		align-items: center;
		overflow: visible;
		/* This does the trick to not make the Sign Up button shrink */

		@media (max-width: $breakpoint-two-rows) {
			justify-content: center;
			padding-left: 8px;
		}
	}

	&.navbar-hide {
		display: none !important;
	}

	&.bg-dark-brown {
		background: #141100;
	}

	&.landing-page {
		background: transparent;

		@media (min-width: $breakpoint-two-rows) {
			height: 56+30px;
			padding-top: 15px;
			margin-bottom: -56-30px;

			.navbar__logo img {
				width: 219px;
			}
		}

		@media (max-width: $breakpoint-two-rows) {
			margin-bottom: 0;
		}

		.landing-hide {
			display: none;
		}
	}

	&--hidden {
		display: none;
	}

	&--absolute {
		position: absolute;
	}

	@media (max-width: $breakpoint-two-rows) {
		height: 105px;
		align-items: flex-end;
	}

	&.navbar__button--watchlist {
		@media (max-width: $breakpoint-two-rows) {
			a {
				color: var(--jw-color-primary) !important;
			}
		}
	}

	&__logo {
		padding-inline-end: 15px;
		display: flex;
		align-items: center;
		flex-shrink: 0;

		img {
			width: 131px;
		}

		@media (max-width: $breakpoint-two-rows) {
			position: absolute;
			top: 0;
			width: 100%;
			min-height: 46px;
			display: flex;
			justify-content: center;
			align-items: center;
			margin: 0;
			padding-inline-end: 0;
		}

		.navbar--is-mobile & {
			justify-content: left;
		}

		.navbar--is-ios & {
			height: 56px;
		}

		&-link {
			display: flex;
			align-items: center;
		}
	}

	&.container &__logo {
		margin-inline-start: 0;
		padding-inline-start: 0;
	}

	&__search {
		z-index: 1;
		flex-grow: 2;
		height: 52px;
		margin: 0;
		margin-inline-end: 7px;
		display: flex;

		.search-suggester .searchbar-input {
			color: $jw-color-white;
			box-shadow: none;
			border-radius: $jw-border-radius;
			font-size: 15px;
			padding: 4px 0;
			padding-inline-start: 50px;
			padding-inline-end: 15px;
		}

		@media (max-width: $screen-md) {
			&--expanded {
				left: 0;
				right: 0;
				position: absolute;
			}
		}
	}

	&.landing-page {
		.navbar__search {
			flex-grow: 0;
			width: 35em;
			transition: width 0.6s;
			transition-delay: 0.1s;
			align-items: center;
			display: flex;
		}

		.navbar__search:focus-within {
			width: 100%;
		}

		@media (max-width: $breakpoint-two-rows) {
			.navbar__search:focus-within {
				display: flex;
				align-items: center;
				justify-content: space-around;
			}
		}
	}

	&__extra {
		display: flex;
		min-width: 92px;

		@media (max-width: $breakpoint-two-rows) {
			display: none;
		}

		&__small {
			display: none;

			@media (max-width: $breakpoint-two-rows) {
				display: block;
				position: absolute;
				top: 0;
				@include inset-inline('end', 0);
				width: 100%;

				&--left {
					display: flex;
					position: absolute;
					@include inset-inline('start', 4px);
				}

				&--right {
					display: flex;
					position: absolute;
					@include inset-inline('end', 0);
				}
			}
		}
	}

	&__extra--remove-spacing {
		min-width: unset;
	}

	&__button {
		height: 56px;
		flex-grow: inherit;
		cursor: pointer;

		a {
			padding: 0;
		}

		> div {
			justify-content: center;
		}

		&__link {
			display: flex;
			align-items: center;
			height: 100%;
			white-space: nowrap;
			font-size: 14px;
			justify-content: center;
			color: $jw-color-mediumgray--light;

			&.router-link-exact-active {
				font-weight: bold;
				color: $jw-color-lightblue--light !important;
			}

			/* We have to force the styles here because Guide technically
			also lives under the /us path (although different subdomain) and
			will be highlighted whenever the Popular route is active */
			&.nav-link-neutral {
				font-weight: normal;
				color: #999c9f !important;

				&:hover {
					color: #d5d5d5 !important;
				}
			}

			&__item {
				margin: 0 15px;
				display: flex;
				margin-inline-end: 15px;

				@media (max-width: $screen-xs) {
					margin: 0px 8px;
				}

				.navbar__button--watchlist &,
				.navbar__button--home & {
					@media (max-width: $breakpoint-two-rows) {
						display: none;
					}
				}

				.navbar__button--sports & {
					@media (max-width: $breakpoint-two-rows) {
						margin: 0px 5px;
					}
				}
			}
		}

		&__icon {
			display: block;

			@media (min-width: $breakpoint-two-rows) {
				margin: 0 5px;
			}

			&.bookmark {
				display: none;

				@media (max-width: $breakpoint-two-rows) {
					display: block;
				}
			}
		}

		&--watchlist {
			@media (max-width: $breakpoint-two-rows) {
				position: absolute;
				top: 0;
				@include inset-inline('end', 60px);
			}
		}

		&--guide-hidden-mobile {
			@media (max-width: $breakpoint-two-rows) {
				display: none;
			}
		}
	}
}
</style>
